import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SxProps } from "@mui/system";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { t } from "i18next";
import { CompareImage, compareBasketState } from "../../recoil/upgrade";
import { getDatasetImageBlob } from "../../services/datasetService";

export const fileName = (path: string) => path.split("/").pop() ?? path;

// 비교 이미지 담기 목록 (프로젝트별). 검증 리스트에서 담고, 디펙 미리보기 풀스크린에서 고른다
export const useCompareBasket = (projectId: string) => {
  const [items, setItems] = useRecoilState(compareBasketState(projectId));
  const has = (path: string) => items.some((item) => item.path === path);
  return {
    items,
    has,
    add: (item: CompareImage) => !has(item.path) && setItems([...items, item]),
    remove: (path: string) => setItems(items.filter((item) => item.path !== path)),
    clear: () => setItems([]),
  };
};

// 데이터셋 경로의 이미지를 인증 요청(blob)으로 받아 표시
export function DatasetImage({ path, sx }: { path: string; sx?: SxProps }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    setSrc(null);
    setFailed(false);
    getDatasetImageBlob(path)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        if (alive) setSrc(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  if (failed)
    return (
      <Typography variant="caption" sx={{ color: "#aeaeb2" }}>
        {t("NoImage")}
      </Typography>
    );
  if (!src) return <CircularProgress size={20} />;
  return <Box component="img" src={src} alt="" title={path} sx={{ objectFit: "contain", ...sx }} />;
}

interface CompareBasketPopoverProps {
  projectId: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect?: (item: CompareImage) => void; // 있으면 항목 클릭으로 선택 (비교 이미지 고르기)
  zIndex?: number; // 풀스크린 Dialog 위에서 열 때
}

// 담긴 비교 이미지 관리: 목록 보기 / 개별 빼기 / 전체 비우기 (+ onSelect가 있으면 골라서 띄우기)
export function CompareBasketPopover({ projectId, anchorEl, onClose, onSelect, zIndex }: CompareBasketPopoverProps) {
  const basket = useCompareBasket(projectId);
  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      // 아이콘 버튼 바로 아래에 붙인다 (높이가 작아야 아래 공간이 모자라 위로 뒤집히지 않는다)
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { mt: 0.5 } } }}
      sx={zIndex ? { zIndex } : undefined}
    >
      <Box sx={{ width: 320, maxHeight: 280, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1, borderBottom: "1px solid var(--dk-border, #eee)" }}>
          <Typography fontWeight={600} sx={{ flex: 1 }}>
            {t("CompareBasket", "비교 이미지 담기")} ({basket.items.length})
          </Typography>
          <Button size="small" color="error" disabled={!basket.items.length} onClick={basket.clear}>
            {t("ClearAll", "전체 비우기")}
          </Button>
        </Box>
        {basket.items.length ? (
          <List dense sx={{ overflowY: "auto" }}>
            {basket.items.map((item) => (
              <ListItem
                key={item.path}
                onClick={onSelect ? () => onSelect(item) : undefined}
                sx={onSelect ? { cursor: "pointer", "&:hover": { bgcolor: "action.hover" } } : undefined}
                secondaryAction={
                  <Tooltip title={t("Remove", "빼기")}>
                    <IconButton edge="end" size="small" onClick={() => basket.remove(item.path)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={fileName(item.path)}
                  secondary={[item.label, item.score !== undefined ? `score ${item.score}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                  primaryTypographyProps={{ noWrap: true, title: item.path }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t("CompareBasketEmptyMsg", "검증 리스트에서 이미지를 오른쪽 클릭해 담을 수 있습니다.")}
          </Typography>
        )}
      </Box>
    </Popover>
  );
}
